import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import BackHomeButton from '../components/BackHomeButton';
import { db, auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

import WorksheetRequestConversation from "../components/WorksheetRequestConversation";

const DETAILS_TEMPLATE = [
  'Class: ',
  'School: ',
  'Chapter: ',
  'Topics: ',
  'Question format: ',
  'Number of questions: ',
  'Other instructions: ',
].join('\n');

function RequestWorksheet() {
  const [issueType, setIssueType] = useState('');
  const [issueDescription, setIssueDescription] = useState(DETAILS_TEMPLATE);
  const [severity, setSeverity] = useState('Medium');
  const [submitting, setSubmitting] = useState(false);
  const [caseList, setCaseList] = useState([]);

  const [casesLoading, setCasesLoading] = useState(true);
const [casesError, setCasesError] = useState("");

  const [teachers, setTeachers] = useState([]);
const [loadingTeachers, setLoadingTeachers] = useState(true);
const [teacherError, setTeacherError] = useState('');
const [selectedTeacher, setSelectedTeacher] = useState(null);
const [teacherReload, setTeacherReload] = useState(0);
const detailsHeadingRef = React.useRef(null);

useEffect(() => {
  let cancelled = false;

  const fetchTeachers = async () => {
    setLoadingTeachers(true);
    setTeacherError('');

    try {
      const snapshot = await getDocs(
        query(
          collection(db, 'profiles'),
          where('persona', '==', 'Teacher')
        )
      );

      const availableTeachers = snapshot.docs
        .map((snapshotDoc) => {
          const profile = snapshotDoc.data();
          return {
            id: snapshotDoc.id,
            displayName: profile.name || profile.displayName || 'Teacher',
            avatarUrl: profile.avatarUrl || '',
            school: profile.school || '',
            city: profile.city || '',
            board: profile.board || '',
            standard: profile.standard || '',
            supportedStandards: profile.supportedStandards || [],
            bio: profile.bio || '',
          };
        })
        .sort((a, b) =>
          String(a.displayName || '').localeCompare(
            String(b.displayName || '')
          )
        );

      if (!cancelled) {
        setTeachers(availableTeachers);
      }
    } catch (error) {
      console.error('Unable to load teachers:', error);

      if (!cancelled) {
        setTeacherError('Unable to load teachers. Please try again.');
      }
    } finally {
      if (!cancelled) {
        setLoadingTeachers(false);
      }
    }
  };

  fetchTeachers();

  return () => {
    cancelled = true;
  };
}, [teacherReload]);

useEffect(() => {
  if (selectedTeacher) {
    detailsHeadingRef.current?.focus();
  }
}, [selectedTeacher]);

  const user = getAuth().currentUser;
  //const db = getFirestore();

useEffect(() => {
  let unsubscribeRequests = () => {};

  const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
    unsubscribeRequests();
    setCaseList([]);
    setCasesError("");

    if (!currentUser) {
      setCasesLoading(false);
      return;
    }

    setCasesLoading(true);

    const requestsQuery = query(
      collection(db, "Worksheet_Requests"),
      where("uid", "==", currentUser.uid)
    );

    unsubscribeRequests = onSnapshot(
      requestsQuery,
      (snapshot) => {
        const requests = snapshot.docs
          .map((requestDoc) => ({
            ...requestDoc.data(),
            id: requestDoc.id,
          }))
          .sort(
            (a, b) =>
              (b.timestamp?.seconds || 0) -
              (a.timestamp?.seconds || 0)
          );

        setCaseList(requests);
        setCasesLoading(false);
        setCasesError("");
      },
      (error) => {
        console.error("Unable to load your requests:", error);
        setCasesError(
          "Unable to load your requests. Please refresh and try again."
        );
        setCasesLoading(false);
      }
    );
  });

  return () => {
    unsubscribeRequests();
    unsubscribeAuth();
  };
}, []);

  const handleSubmit = async () => {

    if (submitting) return;

if (!selectedTeacher) {
  alert('Please select a teacher first.');
  return;
}

if (
  !issueType ||
  !issueDescription.trim() ||
  issueDescription.trim() === DETAILS_TEMPLATE.trim() ||
  !severity
) {
  alert('Please select a subject and fill in your worksheet details.');
  return;
}

    if (!user) {
      alert('Please login first.');
      return;
    }

    const caseId = 'Req-' + Math.floor(100000 + Math.random() * 900000);
    const resolution = "";

    const supportData = {
      uid: user.uid,
      userEmail: user.email,
      requestedTeacherId: selectedTeacher.id,
requestedTeacherName: selectedTeacher.displayName || 'Teacher',
      issueType,
      issueDescription,
      severity,
      caseId,
      status: 'Open',
      timestamp: serverTimestamp(),
      resolution,
    };

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'Worksheet_Requests'), supportData);

      const sendEmail = httpsCallable(functions, 'sendWorksheetEmail');
      const adminEmail = 'eascode.ltd@gmail.com';
      const emailContent = {
    worksheetTitle: `New Worksheet Request: ${caseId}`,
    fileUrl: 'https://eascode.web.app/Old_Home',
    message: `
      A new worksheet request has been submitted.

      Request ID: ${caseId}
      Requested Teacher: ${selectedTeacher.displayName || 'Teacher'}
Requested Teacher UID: ${selectedTeacher.id}
      Name: ${user.displayName || 'Not available'}
      Requester ID: ${user.uid}
      UID: ${user.uid}
      Subject: ${issueType}
      Severity: ${severity}

      Requirement Details:
      ${issueDescription}
    `.trim()
      };

      const notifyAdmin = async () => {
        await sendEmail({
          ...emailContent,
          friendName: 'Eascode Team',
          friendEmail: adminEmail,
        });
      };

      const notifyTeacher = async () => {
        const teacherProfile = await getDoc(
          doc(db, 'profiles', selectedTeacher.id)
        );
        const profileEmail = teacherProfile.exists()
          ? teacherProfile.data().email
          : '';
        const teacherEmail = typeof profileEmail === 'string'
          ? profileEmail.trim()
          : '';

        if (!/^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(teacherEmail)) {
          throw new Error('Selected teacher has no valid email in profiles.');
        }

        // The admin notification also covers a teacher using the same address.
        if (teacherEmail.toLowerCase() === adminEmail) return;

        await sendEmail({
          ...emailContent,
          friendName: selectedTeacher.displayName || 'Teacher',
          friendEmail: teacherEmail,
        });
      };

      const notifications = await Promise.allSettled([
        notifyAdmin(),
        notifyTeacher(),
      ]);
      const recipientLabels = ['admin', 'selected teacher'];
      const failedRecipients = notifications.flatMap((result, index) => {
        if (result.status === 'fulfilled') return [];
        console.error(
          `Worksheet request email failed for ${recipientLabels[index]}:`,
          result.reason
        );
        return [recipientLabels[index]];
      });

      alert(
        `Worksheet request saved! Your Request ID is ${caseId}.` +
        (failedRecipients.length
          ? ` Email notification could not be sent to: ${failedRecipients.join(' and ')}. Please contact support with this Request ID; do not submit it again.`
          : ' Email notifications sent.')
      );
      setIssueType('');
      setIssueDescription(DETAILS_TEMPLATE);
      setSeverity('Medium');
      setSelectedTeacher(null);
    } catch (error) {
      console.error(error);
      alert('Something went wrong.');
    }




    setSubmitting(false);
  };

  const formatRequestDate = (timestamp) => {
  if (!timestamp) return "Not available";

  const date =
    typeof timestamp.toDate === "function"
      ? timestamp.toDate()
      : timestamp.seconds != null
        ? new Date(timestamp.seconds * 1000)
        : new Date(timestamp);

  if (Number.isNaN(date.getTime())) return "Not available";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
};

  return (
    <div className="worksheet-page">
      <style>{worksheetCss}</style>
      <div className="worksheet-shell">
      <BackHomeButton />

<header className="worksheet-header">
  <p className="worksheet-eyebrow">PERSONALISED PRACTICE</p>
  <h2>Request a worksheet</h2>
  <p className="worksheet-subtitle">
    Choose a teacher and tell us what your child needs to practise.
  </p>
  <ol className="worksheet-steps" aria-label="Request progress">
    <li
      className={!selectedTeacher ? 'is-current' : 'is-complete'}
      aria-current={!selectedTeacher ? 'step' : undefined}
    >
      <span aria-hidden="true">{selectedTeacher ? '✓' : '1'}</span>
      Choose teacher
    </li>
    <li
      className={selectedTeacher ? 'is-current' : ''}
      aria-current={selectedTeacher ? 'step' : undefined}
    >
      <span aria-hidden="true">2</span>
      Worksheet details
    </li>
  </ol>
</header>

{!selectedTeacher ? (
  <section className="worksheet-section" aria-labelledby="teacher-selection-heading">
    <h3 id="teacher-selection-heading">Choose a teacher</h3>

    <p style={styles.helperText}>
      Browse teacher profiles below.
      Select a teacher to enter your requirements.
    </p>

    {loadingTeachers ? (
      <p role="status">Loading teachers...</p>
    ) : teacherError ? (
      <div role="alert" style={styles.infoBox}>
        <p>{teacherError}</p>
        <button
          type="button"
          style={styles.secondaryButton}
          onClick={() => setTeacherReload((value) => value + 1)}
        >
          Try again
        </button>
      </div>
    ) : teachers.length === 0 ? (
      <div style={styles.infoBox}>
        No teacher profiles were found. Please contact support if you
        expected a teacher to appear.
      </div>
    ) : (
      <div
        role="region"
        aria-label="Teacher profiles"
        tabIndex={0}
        className="worksheet-teacher-list"
      >
        {teachers.map((teacher) => {
          const standards = [
            ...new Set(
              [
                teacher.standard,
                ...(Array.isArray(teacher.supportedStandards)
                  ? teacher.supportedStandards
                  : []),
              ]
                .filter(
                  (value) =>
                    value !== undefined &&
                    value !== null &&
                    value !== ''
                )
                .map(String)
            ),
          ];

          return (
            <article key={teacher.id} className="worksheet-teacher-card">
              <div style={styles.teacherHeader}>
                <div style={styles.teacherAvatar}>
                  {teacher.avatarUrl ? (
                    <img
                      src={teacher.avatarUrl}
                      alt=""
                      style={styles.teacherPhoto}
                    />
                  ) : (
                    <span aria-hidden="true">
                      {String(teacher.displayName || 'T')
                        .charAt(0)
                        .toUpperCase()}
                    </span>
                  )}
                </div>

                <div style={{ minWidth: 0 }}>
                  <h4 style={styles.teacherName}>
                    {teacher.displayName || 'Teacher'}
                  </h4>
                  <p style={styles.teacherLocation}>
                    {[teacher.school, teacher.city]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
              </div>

              <span style={styles.availabilityBadge}>
                Teacher
              </span>

              {teacher.bio && (
                <p style={styles.teacherBio}>{teacher.bio}</p>
              )}

              <button
                type="button"
                className="worksheet-primary-button"
                onClick={() => setSelectedTeacher(teacher)}
                aria-label={`Select ${
                  teacher.displayName || 'teacher'
                } for your worksheet request`}
              >
                Select this teacher
              </button>
            </article>
          );
        })}
      </div>
    )}
  </section>
) : (
  <section className="worksheet-section" aria-labelledby="worksheet-details-heading">
    <h3
      id="worksheet-details-heading"
      ref={detailsHeadingRef}
      tabIndex={-1}
    >
      Tell us about your worksheet
    </h3>

    <div style={styles.infoBox}>
      <p style={{ marginTop: 0 }}>
        Requested teacher:{' '}
        <strong>{selectedTeacher.displayName || 'Teacher'}</strong>
      </p>

      <button
        type="button"
        style={styles.secondaryButton}
        disabled={submitting}
        onClick={() => setSelectedTeacher(null)}
      >
        Change teacher
      </button>
    </div>

    <p style={styles.helperText}>
      Include the class, board, chapter, and question format you need.
      Your teacher preference will be included with the request.
    </p>

    <div style={styles.inputGroup}>
      <label htmlFor="worksheet-subject" style={styles.label}>
        Subject
      </label>
      <select
        id="worksheet-subject"
        value={issueType}
        onChange={(event) => setIssueType(event.target.value)}
        disabled={submitting}
        style={styles.select}
      >
        <option value="">Select Subject</option>
        <option value="English Language">English Language</option>
        <option value="English Literature">English Literature</option>
        <option value="Maths">Maths</option>
        <option value="Hindi Language">Hindi Language</option>
        <option value="Hindi Literature">Hindi Literature</option>
        <option value="E.V.S.">E.V.S.</option>
        <option value="Moral Science">Moral Science</option>
        <option value="Other">Other</option>
      </select>
    </div>

    <div style={styles.inputGroup}>
      <label htmlFor="worksheet-details" style={styles.label}>
        Requirement Details
      </label>
<textarea
  id="worksheet-details"
  value={issueDescription}
  onChange={(event) => setIssueDescription(event.target.value)}
  disabled={submitting}
  style={{
    ...styles.input,
    minHeight: '240px',
    boxSizing: 'border-box',
    resize: 'vertical',
  }}
/>
    </div>

    <button
      type="button"
      onClick={handleSubmit}
      disabled={submitting}
      className="worksheet-primary-button worksheet-submit"
    >
      {submitting ? 'Submitting...' : 'Submit Worksheet Request'}
    </button>
  </section>
)}

<section
  aria-labelledby="my-requests-heading"
  style={{ marginTop: "32px" }}
>
  <h3
    id="my-requests-heading"
    style={{
      marginBottom: "16px",
      fontSize: "18px",
      fontWeight: 700,
      color: "#0f172a",
    }}
  >
    Your Requests
  </h3>

  {casesLoading ? (
    <p role="status">Loading your requests...</p>
  ) : casesError ? (
    <p role="alert" style={{ color: "#b91c1c" }}>
      {casesError}
    </p>
  ) : caseList.length === 0 ? (
    <p style={{ color: "#64748b" }}>
      You have not submitted any worksheet requests yet.
    </p>
  ) : (
    <div style={{ display: "grid", gap: "14px" }}>
      {caseList.map((request) => {
        const completed = request.status === "Completed";
        const denied = request.status === "Denied";

        const closed = completed || denied;

const closureDate = completed
  ? request.completedAt
  : denied
    ? request.deniedAt
    : null;

        return (
          <article
            key={request.id}
            style={{
              minWidth: 0,
              padding: "18px",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              backgroundColor: "#ffffff",
              overflowWrap: "anywhere",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <strong style={{ color: "#0f172a" }}>
                {request.caseId || request.id}
              </strong>

              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: 600,
                  backgroundColor: completed
                    ? "#dcfce7"
                    : denied
                      ? "#fee2e2"
                      : "#eff6ff",
                  color: completed
                    ? "#166534"
                    : denied
                      ? "#991b1b"
                      : "#1d4ed8",
                }}
              >
                {request.status || "Open"}
              </span>
            </div>

            <p
              style={{
                marginTop: "12px",
                fontSize: "14px",
                color: "#334155",
              }}
            >
              <strong>Subject:</strong>{" "}
              {request.issueType || "Worksheet"}
            </p>

<div
  style={{
    display: "grid",
    gap: "8px",
    marginTop: "12px",
    padding: "12px",
    borderRadius: "10px",
    backgroundColor: "#f8fafc",
    fontSize: "13px",
    lineHeight: 1.6,
    color: "#475569",
  }}
>
  <p style={{ margin: 0 }}>
    <strong>
      {completed ? "Worksheet provided by:" : "Selected teacher:"}
    </strong>{" "}
{request.requestedTeacherName || "Not available"}
  </p>

  <p style={{ margin: 0 }}>
    <strong>Request created:</strong>{" "}
    {formatRequestDate(request.timestamp)}
  </p>

  <p style={{ margin: 0 }}>
    <strong>
      {completed
        ? "Request completed:"
        : denied
          ? "Request declined:"
          : "Request closed:"}
    </strong>{" "}
    {closed ? formatRequestDate(closureDate) : "Not closed yet"}
  </p>

  <p style={{ margin: 0, fontSize: "11px", color: "#64748b" }}>
    All times are in IST.
  </p>
</div>

            {request.resolution && (
              <p
                style={{
                  marginTop: "12px",
                  whiteSpace: "pre-wrap",
                  fontSize: "14px",
                  lineHeight: 1.6,
                  color: "#475569",
                }}
              >
                {request.resolution}
              </p>
            )}

            {completed && request.worksheetDownloadUrl && (
              <a
                href={request.worksheetDownloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  maxWidth: "100%",
                  minHeight: "44px",
                  marginTop: "16px",
                  padding: "12px 18px",
                  borderRadius: "10px",
                  backgroundColor: "#2563eb",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 600,
                  textDecoration: "none",
                  textAlign: "center",
                }}
              >
                Download Worksheet
              </a>
            )}

            {completed && !request.worksheetDownloadUrl && (
              <p
                style={{
                  marginTop: "12px",
                  fontSize: "13px",
                  color: "#92400e",
                }}
              >
                The download link is unavailable. Please contact
                Eascode with this request ID.
              </p>
            )}

            <WorksheetRequestConversation
              request={request}
              viewerRole="requester"
            />

          </article>
        );
      })}
    </div>
  )}
</section>

      <footer style={styles.footer}>
        <p style={styles.footerText}>© 2026 Eascode All Rights Reserved.</p>
      </footer>
      </div>
    </div>
  );
}

const worksheetCss = `
  .worksheet-page {
    min-height: 100vh;
    padding: 20px 12px 40px;
    background: #f4f7fb;
    color: #172033;
    font-family: 'Segoe UI', Tahoma, sans-serif;
    line-height: 1.5;
  }
  .worksheet-page *,
  .worksheet-page *::before,
  .worksheet-page *::after { box-sizing: border-box; }
  .worksheet-shell {
    width: 100%;
    max-width: 760px;
    margin: 0 auto;
    padding: 24px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    box-shadow: 0 12px 36px rgba(15, 23, 42, 0.05);
  }
  .worksheet-header { margin: 28px 0; }
  .worksheet-eyebrow {
    margin: 0 0 8px;
    color: #2563eb;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
  }
  .worksheet-header h2 {
    margin: 0;
    color: #0f172a;
    font-size: clamp(26px, 4vw, 34px);
    font-weight: 750;
    line-height: 1.2;
    letter-spacing: -0.025em;
  }
  .worksheet-subtitle {
    max-width: 520px;
    margin: 12px 0 0;
    color: #64748b;
    font-size: 15px;
    line-height: 1.7;
  }
  .worksheet-steps {
    display: flex;
    gap: 20px;
    margin: 24px 0 0;
    padding: 16px 0;
    list-style: none;
    border-top: 1px solid #eef2f7;
    border-bottom: 1px solid #eef2f7;
  }
  .worksheet-steps li {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
  }
  .worksheet-steps li > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 50%;
    background: #f1f5f9;
    color: #64748b;
  }
  .worksheet-steps .is-current { color: #1d4ed8; }
  .worksheet-steps .is-current > span { background: #2563eb; color: #ffffff; }
  .worksheet-steps .is-complete > span { background: #dcfce7; color: #166534; }
  .worksheet-section > h3 {
    margin: 0 0 8px;
    color: #0f172a;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.4;
  }
  .worksheet-teacher-list {
    display: grid;
    gap: 14px;
    max-height: 560px;
    overflow-y: auto;
    scroll-snap-type: y proximity;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f8fafc;
    padding: 6px;
    scroll-padding-block: 6px;
    border-radius: 14px;
    background: #f8fafc;
  }
  .worksheet-teacher-card {
    min-width: 0;
    scroll-snap-align: start;
    padding: 20px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    overflow-wrap: anywhere;
    transition: border-color 160ms ease, box-shadow 160ms ease;
  }
  .worksheet-teacher-card:focus-within {
    border-color: #93c5fd;
    box-shadow: 0 4px 16px rgba(37, 99, 235, 0.08);
  }
  .worksheet-primary-button {
    display: block;
    width: 100%;
    min-height: 46px;
    margin-top: 18px;
    padding: 12px 18px;
    border: 1px solid #2563eb;
    border-radius: 10px;
    background: #2563eb;
    color: #ffffff;
    font-family: inherit;
    font-size: 14px;
    font-weight: 650;
    line-height: 1.4;
    cursor: pointer;
    transition: background-color 160ms ease;
  }
  .worksheet-primary-button:disabled { opacity: 0.6; cursor: wait; }
  .worksheet-page button:focus-visible,
  .worksheet-page select:focus-visible,
  .worksheet-page textarea:focus-visible,
  .worksheet-teacher-list:focus-visible {
    outline: 3px solid #93c5fd;
    outline-offset: 3px;
  }
  .worksheet-page textarea::placeholder { color: #64748b; opacity: 1; }
  .worksheet-page table th,
  .worksheet-page table td {
    padding: 12px 8px;
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid #e2e8f0;
    overflow-wrap: anywhere;
  }
  .worksheet-page table th {
    background: #f8fafc;
    color: #475569;
    font-size: 12px;
    font-weight: 650;
  }
  @media (hover: hover) {
    .worksheet-teacher-card:hover { border-color: #bfdbfe; }
    .worksheet-primary-button:hover:not(:disabled) { background: #1d4ed8; }
  }
  @media (min-width: 768px) {
    .worksheet-page { padding: 40px 24px 64px; }
    .worksheet-shell { padding: 36px 40px; }
    .worksheet-teacher-card { padding: 24px; }
    .worksheet-teacher-card .worksheet-primary-button {
      width: auto;
      min-width: 180px;
      margin-left: auto;
    }
    .worksheet-submit { width: auto; min-width: 240px; margin-left: auto; }
  }
  @media (max-width: 380px) {
    .worksheet-shell { padding: 18px 14px; }
    .worksheet-steps { gap: 12px; }
    .worksheet-steps li { gap: 6px; font-size: 12px; }
    .worksheet-teacher-card { padding: 16px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .worksheet-page * { transition: none; }
  }
`;

const styles = {

  helperText: {
  color: '#64748b',
  fontSize: '14px',
  lineHeight: 1.7,
  margin: '0 0 20px',
},
teacherCarousel: {
  maxHeight: '480px',
  overflowY: 'auto',
  scrollSnapType: 'y proximity',
  padding: '4px 8px 4px 4px',
  border: '1px solid #e2e8f0',
  borderRadius: '14px',
  backgroundColor: '#f8fafc',
},
teacherCard: {
  scrollSnapAlign: 'start',
  backgroundColor: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '18px',
  marginBottom: '12px',
  overflowWrap: 'anywhere',
},
teacherHeader: {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '14px',
},
teacherAvatar: {
  width: '56px',
  height: '56px',
  flexShrink: 0,
  borderRadius: '50%',
  backgroundColor: '#dbeafe',
  color: '#1d4ed8',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '24px',
  fontWeight: 700,
  overflow: 'hidden',
},
teacherPhoto: {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
},
teacherName: {
  margin: 0,
  fontSize: '1.1rem',
  color: '#0f172a',
},
teacherLocation: {
  margin: '4px 0 0',
  fontSize: '0.85rem',
  color: '#64748b',
},
availabilityBadge: {
  display: 'inline-block',
  backgroundColor: '#dcfce7',
  color: '#166534',
  padding: '4px 9px',
  borderRadius: '20px',
  fontSize: '0.75rem',
  fontWeight: 600,
},
teacherBio: {
  fontSize: '0.9rem',
  color: '#475569',
  lineHeight: 1.6,
  whiteSpace: 'pre-line',
},
teacherMeta: {
  fontSize: '0.85rem',
  color: '#475569',
  margin: '8px 0',
},
secondaryButton: {
  minHeight: '44px',
  backgroundColor: '#ffffff',
  color: '#1d4ed8',
  border: '1px solid #bfdbfe',
  borderRadius: '8px',
  padding: '10px 14px',
  fontFamily: 'inherit',
  fontSize: '13px',
  cursor: 'pointer',
  fontWeight: 600,
},

  card: {
    maxWidth: '480px',
    margin: '2rem auto',
    padding: '2rem',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  heading: {
    textAlign: 'center',
    fontSize: '1.8rem',
    fontWeight: '700',
    marginBottom: '1.5rem',
    color: '#333',
  },
  infoBox: {
    backgroundColor: '#eff6ff',
    border: '1px solid #dbeafe',
    padding: '16px',
    borderRadius: '12px',
    marginBottom: '20px',
    fontSize: '14px',
    color: '#1e3a5f',
  },
  inputGroup: {
    marginBottom: '22px',
  },
  label: {
    fontWeight: '600',
    color: '#334155',
    fontSize: '14px',
    marginBottom: '8px',
    display: 'block',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #cbd5e1',
    borderRadius: '10px',
    backgroundColor: '#ffffff',
    color: '#0f172a',
    fontFamily: 'inherit',
    fontSize: '16px',
    lineHeight: 1.6,
  },
  select: {
    width: '100%',
    minHeight: '48px',
    padding: '12px 14px',
    border: '1px solid #cbd5e1',
    borderRadius: '10px',
    backgroundColor: '#ffffff',
    color: '#0f172a',
    fontFamily: 'inherit',
    fontSize: '16px',
  },
  button: {
    width: '100%',
    padding: '0.8rem',
    backgroundColor: '#4285f4',
    color: '#fff',
    fontSize: '1.1rem',
    border: 'none',
    borderRadius: '10px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 10px rgba(66,133,244,0.4)',
    marginTop: '1rem',
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
    marginTop: '12px',
    fontSize: '13px',
  },
  footer: {
    marginTop: '32px',
    paddingTop: '20px',
    borderTop: '1px solid #e2e8f0',
    textAlign: 'center',
  },
  footerText: {
    margin: 0,
    color: '#64748b',
    fontSize: '12px',
  },
};

export default RequestWorksheet;
